/**
 * QuickActions - Context-aware action suggestions
 * Shows 2-3 most important actions based on portfolio state
 */

import React from 'react';
import { AlertTriangle, TrendingUp, Calendar, CheckCircle } from 'lucide-react';

export interface QuickAction {
  type: 'rebalance' | 'income' | 'alert' | 'success';
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
}

export interface QuickActionsProps {
  actions: QuickAction[];
  className?: string;
}

const QuickActions: React.FC<QuickActionsProps> = ({ actions, className = '' }) => {
  if (actions.length === 0) {
    return null;
  }

  const getIcon = (type: QuickAction['type']) => {
    switch (type) {
      case 'rebalance':
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case 'income':
        return <Calendar className="w-5 h-5 text-blue-500" />;
      case 'alert':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      default:
        return <TrendingUp className="w-5 h-5 text-gray-500" />;
    }
  };

  const getBgColor = (type: QuickAction['type']) => {
    switch (type) {
      case 'rebalance':
        return 'bg-orange-50 border-orange-200';
      case 'income':
        return 'bg-blue-50 border-blue-200';
      case 'alert':
        return 'bg-red-50 border-red-200';
      case 'success':
        return 'bg-green-50 border-green-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
      <div className="space-y-3">
        {actions.map((action, index) => (
          <div
            key={index}
            className={`flex items-start gap-3 p-4 rounded-lg border ${getBgColor(action.type)}`}
          >
            <div className="flex-shrink-0 mt-0.5">
              {getIcon(action.type)}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-gray-900 mb-1">
                {action.title}
              </h4>
              <p className="text-sm text-gray-600 mb-2">
                {action.description}
              </p>
              {action.actionLabel && action.onAction && (
                <button
                  onClick={action.onAction}
                  className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {action.actionLabel} →
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default QuickActions;
